import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { formatNumber, formatDisplayURL } from '../utils/contestUtils';
import { withdrawContestEntry } from '../../../utils/api';
import useContestDetails from '../hooks/useContestDetails';
import useModalOutsideClick from '../hooks/useModalOutsideClick';
import { useAuth } from '../../../context/AuthContext';
import EntrantPayoutSection from './EntrantPayoutSection';
import ViewEntrantsModal from './modals/ViewEntrantsModal';
import ViewEntryModal from './modals/ViewEntryModal';
import ViewCurrentChipCounts from './modals/ViewCurrentChipCounts';
import ViewPlayerPayoutsModal from './modals/ViewPlayerPayoutsModal';
import ViewGolfLeaderboardModal from './modals/ViewGolfLeaderboardModal';
import ViewOwnershipModal from './modals/ViewOwnershipModal';
import './ContestDetails.css';
import { useNavigate } from 'react-router-dom';

const ContestDetails = ({ contest, showEnterButton }) => {
    const { logout } = useAuth();
    const {
        isUserEntered,
        handleWithdraw,
        showModal,
        setShowModal,
        modalContent,
        showEntrantsModal,
        setShowEntrantsModal,
        currentUsername,
        handleEnterContest,
        handleViewEntries
    } = useContestDetails(contest);

    const [selectedEntry, setSelectedEntry] = useState(null);
    const [showViewEntryModal, setShowViewEntryModal] = useState(false);
    const [isWithdrawing, setIsWithdrawing] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [showOnlyUserEntries, setShowOnlyUserEntries] = useState(false);
    const [showChipCountsModal, setShowChipCountsModal] = useState(false);
    const [showPlayerPayoutsModal, setShowPlayerPayoutsModal] = useState(false);
    const [showGolfLeaderboardModal, setShowGolfLeaderboardModal] = useState(false);
    const [showOwnershipModal, setShowOwnershipModal] = useState(false);

    const navigate = useNavigate();

    // Use the custom hook to handle modal closing on outside click
    useModalOutsideClick(showModal, setShowModal);

    const userPlayerNames = useMemo(() => {
        if (!currentUsername || !contest.entrants) {
            return [];
        }
        const lowerCaseUsername = currentUsername.toLowerCase();
        const playerNames = contest.entrants
            .filter(entrant => entrant.username.toLowerCase() === lowerCaseUsername)
            .flatMap(entry => {
                if (!entry.team) return [];
                const players = [];
                if (entry.team.captain) {
                    players.push(entry.team.captain.toLowerCase());
                }
                if (entry.team.flexPlayers) {
                    players.push(...entry.team.flexPlayers.map(p => p.toLowerCase()));
                }
                return players;
            });
        
        return [...new Set(playerNames)];
    }, [contest.entrants, currentUsername]);

    const shouldShowEnterButton = showEnterButton && contest.status === 'Registering';
    const tournamentURL = contest.tournamentURL.startsWith('http') ? contest.tournamentURL : `https://${contest.tournamentURL}`;
    const displayURL = formatDisplayURL(contest.tournamentURL);

    // Check if user has entries in this contest
    const hasUserEntries = contest.entrants.some(entrant => 
        entrant.username.toLowerCase() === currentUsername?.toLowerCase()
    );

    const handleEntryClick = (entry) => {
        setSelectedEntry(entry);
        setShowViewEntryModal(true);
    };

    const handleWithdrawClick = () => {
        setShowOnlyUserEntries(true);
        handleViewEntries();
    };

    const handleCloseEntrantsModal = () => {
        setShowEntrantsModal(false);
        setShowOnlyUserEntries(false);
    };

    const handleWithdrawEntry = async () => {
        if (!selectedEntry || isWithdrawing) return;
        
        setIsWithdrawing(true);
        setIsSuccess(false);
        
        try {
            await withdrawContestEntry(contest._id, selectedEntry.entryName);
            
            // Show success state (checkmark)
            setIsSuccess(true);
            
            // Add a delay to show the checkmark before closing the modal
            setTimeout(() => {
                setShowViewEntryModal(false);
                // Refresh the page to show updated contest details
                window.location.reload();
            }, 1500);
        } catch (error) {
            console.error('Error in handleWithdrawEntry:', error);
            console.log('Error message:', error.message);
            
            // Close both modals immediately
            setShowViewEntryModal(false);
            setShowEntrantsModal(false);
            
            // Check if it's an authentication error
            if (error.message.includes('session has expired')) {
                console.log('Detected session expired, calling logout');
                // Call logout directly instead of throwing
                logout();
            } else {
                // For other errors, show the alert with the specific error message
                console.log('Showing alert with message:', error.message);
                alert(error.message);
            }
            
            setIsSuccess(false);
        } finally {
            // Only reset isWithdrawing if there was an error
            // For success, we keep the button disabled until reload
            if (!isSuccess) {
                setIsWithdrawing(false);
            }
        }
    };

    return (
        <div className="contest-details-container">
            <div className="contest-info-header">
                <h2>{contest.tournamentName}</h2>
            </div>
            <div className="contest-basic-info">
                <div className="contest-info-section">
                    <p>Deadline: <span className="contest-info-values">
                        {contest.deadline ? new Date(contest.deadline).toLocaleString() : 'TBD'}
                    </span></p>
                    <p>Entry Fee: <span className="contest-info-values">${contest.entryFee + contest.adminFee}</span></p>
                    {contest.draftType !== 'Snake' && (
                        <p>Salary Cap: <span className="contest-info-values">${formatNumber(contest.salaryCap)}</span></p>
                    )}
                    <p>Draft Type: <span className="contest-info-values">{contest.draftType}</span></p>
                    <p>Format: <span className="contest-info-values">{contest.format}</span></p>
                    <p>Max Entries: <span className="contest-info-values">{contest.maxEntries}</span></p>
                    <p>Links: <span className="contest-info-values">
                        <button className="details-links-text" onClick={() => window.open(tournamentURL, '_blank')}>Info</button> - 
                        <button className="details-links-text" onClick={handleViewEntries}>Entries ({contest.entrants.length})</button>
                        {contest.status === 'Running' && (
                            <>
                                {contest.sport === 'Poker' && (
                                    <> -<button className="details-links-text" onClick={() => setShowChipCountsModal(true)}>Chips</button> -
                                    <button className="details-links-text" onClick={() => setShowPlayerPayoutsModal(true)}>Payouts</button></>
                                )}
                                {contest.sport === 'Golf' && (
                                    <> - <button className="details-links-text" onClick={() => setShowGolfLeaderboardModal(true)}>Leaderboard</button></>
                                )} - <button className="details-links-text" onClick={() => setShowOwnershipModal(true)}>Ownership</button>
                            </>
                        )}
                        {contest.status === 'Completed' && (
                            <> - <button className="details-links-text" onClick={() => setShowOwnershipModal(true)}>Ownership</button></>
                        )}
                    </span></p>
                    {shouldShowEnterButton && (
                        <div className="button-group">
                            {contest.draftType === 'Snake' ? (
                                <>
                                    {contest.status === 'Drafting' ? (
                                        <button className="enter-draft-button" onClick={() => navigate(`/draft/${contest._id}`)}>
                                            Enter Draft Room
                                        </button>
                                    ) : (
                                        <>
                                            {isUserEntered ? (
                                                <button className="withdraw-contest-button" onClick={handleWithdraw}>Withdraw</button>
                                            ) : (
                                                <button className="enter-contest-button" onClick={handleEnterContest}>Enter</button>
                                            )}
                                        </>
                                    )}
                                </>
                            ) : (
                                <>
                                    <button className="enter-contest-button" onClick={handleEnterContest}>Enter</button>
                                    {hasUserEntries && (
                                        <button className="withdraw-contest-button" onClick={handleWithdrawClick}>Withdraw</button>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
                <EntrantPayoutSection contest={contest} />
            </div>
            {showModal && (
                <div className="modal">
                    <div className="modal-content">
                        <span className="close" onClick={() => setShowModal(false)}>&times;</span>
                        {modalContent}
                    </div>
                </div>
            )}
            <ViewEntrantsModal
                show={showEntrantsModal}
                onClose={handleCloseEntrantsModal}
                entrants={contest.entrants || []}
                players={contest.players || []}
                currentUsername={currentUsername}
                onEntryClick={handleEntryClick}
                contestStatus={contest.status}
                sport={contest.sport}
                showOnlyUserEntries={showOnlyUserEntries}
                customTitle={null}
            />
            <ViewEntryModal
                show={showViewEntryModal}
                onClose={() => setShowViewEntryModal(false)}
                onWithdraw={handleWithdrawEntry}
                entry={selectedEntry}
                contest={contest}
                isWithdrawing={isWithdrawing}
                isSuccess={isSuccess}
                userPlayers={userPlayerNames}
            />
            <ViewCurrentChipCounts
                show={showChipCountsModal}
                onClose={() => setShowChipCountsModal(false)}
                players={contest.players || []}
                currentUsername={currentUsername}
                userPlayerNames={userPlayerNames}
            />
            <ViewPlayerPayoutsModal
                show={showPlayerPayoutsModal}
                onClose={() => setShowPlayerPayoutsModal(false)}
                players={contest.players || []}
                playerPayouts={contest.playerPayouts || []}
                userPlayerNames={userPlayerNames}
            />
            <ViewGolfLeaderboardModal
                show={showGolfLeaderboardModal}
                onClose={() => setShowGolfLeaderboardModal(false)}
                players={contest.players || []}
                userPlayerNames={userPlayerNames}
            />
            <ViewOwnershipModal
                show={showOwnershipModal}
                onClose={() => setShowOwnershipModal(false)}
                players={contest.players || []}
                entrants={contest.entrants || []}
                currentUsername={currentUsername}
                contest={contest}
                userPlayerNames={userPlayerNames}
            />
        </div>
    );
};

ContestDetails.propTypes = {
  contest: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    tournamentName: PropTypes.string.isRequired,
    deadline: PropTypes.string,
    entryFee: PropTypes.number.isRequired,
    adminFee: PropTypes.number.isRequired,
    salaryCap: PropTypes.number,
    draftType: PropTypes.string,
    maxEntries: PropTypes.number,
    format: PropTypes.string.isRequired,
    tournamentURL: PropTypes.string.isRequired,
    entrants: PropTypes.array.isRequired,
    status: PropTypes.string.isRequired,
    sport: PropTypes.string.isRequired,
    players: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      chipCount: PropTypes.number,
      salary: PropTypes.number,
      finish: PropTypes.number,
      sportSpecificData: PropTypes.shape({
        currentChipCount: PropTypes.number
      })
    })).isRequired,
    playerPayouts: PropTypes.arrayOf(PropTypes.number).isRequired
  }).isRequired,
  showEnterButton: PropTypes.bool.isRequired
};

export default ContestDetails;
